import { CHECKOUT_THEME_QUERY_PARAM, DINEOUT_CHECKOUT_THEME_NAME, getCheckoutThemeName } from "@swiggy-private/checkout-core";
import { ITheme } from "@swiggy-private/dls-web-common/dist/components/types";
import { themeCssMap } from "@swiggy-private/dls-web-common/dist/components/themes/precomputed/json/theme-map";
import { BaseTheme } from "@swiggy-private/dls-web-common/dist/components/themes/precomputed/Base/BaseTheme";
import { DineoutTheme } from "@swiggy-private/dls-web-common/dist/components/themes/precomputed/Dineout/DineoutTheme";

/** DLS CSS bundle keys accepted by `loadTheme()` from dls-web-theme. */
export type CheckoutCssThemeName = keyof typeof themeCssMap;

/**
 * Everything the checkout shell needs to render one theme. A theme is picked by
 * *name* (from `?theme=…`); each name maps to exactly one descriptor here, so
 * turning a theme on/off or adding a new one ("dark", "diwali", …) is a single
 * row in {@link CHECKOUT_THEMES} — no `isXTheme ? … : …` branches sprinkled
 * across components.
 *
 * Exported because it is the return/param type of the public `resolveCheckoutTheme`
 * / `isBaseCheckoutTheme` / `isDineoutCheckoutTheme` helpers (needed for declaration emit).
 */
export interface CheckoutThemeDescriptor {
    /** Registry key — matches the `?theme=` URL value / stamped html attr value. */
    name: string;
    /** DLS theme object handed to `<ThemeProvider>`. */
    theme: ITheme;
    /**
     * DLS theme CSS bundle name for `loadTheme()`. `undefined` = nothing extra to
     * load (base theme CSS already ships with the app bundle).
     */
    cssThemeName?: CheckoutCssThemeName;
    /**
     * Pins the colorMode regardless of the OS/URL `darkTheme` signal. `undefined`
     * = follow the existing dark/light resolution (`?darkTheme=` / `html` class).
     */
    forcedColorMode?: "light" | "dark";
    /**
     * TODO: Dark help icon for cream/light header surfaces. When set, overrides
     * API `helpIconImage` (white Help.png) on sticky header / themed checkout.
     */
    helpIconImageOnLightSurface?: string;
}

/** Registry key for the default (unthemed) checkout. */
const BASE_CHECKOUT_THEME_NAME = "base";

/**
 * Central registry of every checkout theme, keyed by its `?theme=` name. `base`
 * is the default row, so resolution is a single lookup with no special-casing.
 * Adding a theme ("dark", "diwali", …) is one row here.
 */
const CHECKOUT_THEMES: Record<string, CheckoutThemeDescriptor> = {
    [BASE_CHECKOUT_THEME_NAME]: {
        name: BASE_CHECKOUT_THEME_NAME,
        theme: BaseTheme,
    },
    [DINEOUT_CHECKOUT_THEME_NAME]: {
        name: DINEOUT_CHECKOUT_THEME_NAME,
        theme: DineoutTheme,
        cssThemeName: "dineout",
        forcedColorMode: "light",
    },
};

/**
 * Maps checkout theme *names* (`?theme=` / html attr) to DLS CSS bundle keys.
 * Derived from {@link CHECKOUT_THEMES} — add a theme row there, not here.
 */
export const checkoutThemeCssRegistry: Record<string, CheckoutCssThemeName> = Object.fromEntries(
    Object.values(CHECKOUT_THEMES)
        .filter((descriptor): descriptor is CheckoutThemeDescriptor & { cssThemeName: CheckoutCssThemeName } =>
            Boolean(descriptor.cssThemeName),
        )
        .map((descriptor) => [descriptor.name, descriptor.cssThemeName]),
) as Record<string, CheckoutCssThemeName>;

/**
 * Map the active theme name to its descriptor, falling back to `base` when no
 * (known) theme is active.
 *
 * Pass `ssrThemeName` only for the server render (from the HTML/template layer).
 * On the client, theme is read from the `data-checkout-theme` html attr stamped
 * by head.html — never from the URL query string.
 */
export const resolveCheckoutTheme = (ssrThemeName?: string | null): CheckoutThemeDescriptor => {
    const name = ssrThemeName ?? getCheckoutThemeName() ?? BASE_CHECKOUT_THEME_NAME;
    if (Object.prototype.hasOwnProperty.call(CHECKOUT_THEMES, name)) {
        return CHECKOUT_THEMES[name];
    }
    return CHECKOUT_THEMES[BASE_CHECKOUT_THEME_NAME];
};

/** True for the default (unthemed) checkout (SSR-safe, no client mount gate needed). */
export const isBaseCheckoutTheme = (descriptor: CheckoutThemeDescriptor): boolean =>
    descriptor.name === BASE_CHECKOUT_THEME_NAME;

/** True when the active (or given) theme is the Dineout product theme (not base checkout). */
export const isDineoutCheckoutTheme = (name?: string | null): boolean =>
    (name ?? getCheckoutThemeName()) === DINEOUT_CHECKOUT_THEME_NAME;

/** Coupon modal close-icon bg on Umami — matches dineout `--color-background_Secondary`. */
export const DINEOUT_CART_MODAL_CLOSE_ICON_BG = "var(--color-background_Secondary, #F5EDE0)";

const EXTERNAL_WIDGET_INNER_URL_PARAM = "url";

/** Stamps the active checkout theme on the inner `url=` of an externalWidget deeplink. */
export const appendThemeToExternalWidgetUrl = (deeplink: string): string => {
    const activeTheme = getCheckoutThemeName();
    if (!activeTheme) {
        return deeplink;
    }

    try {
        const outerDeeplinkBaseUrl = new URL(deeplink);
        const innerWebviewBaseUrl = outerDeeplinkBaseUrl.searchParams.get(EXTERNAL_WIDGET_INNER_URL_PARAM);
        if (!innerWebviewBaseUrl) {
            return deeplink;
        }

        const innerWebviewUrl = new URL(innerWebviewBaseUrl);
        innerWebviewUrl.searchParams.set(CHECKOUT_THEME_QUERY_PARAM, activeTheme);

        const updatedInnerWebviewUrl = innerWebviewUrl.toString();
        outerDeeplinkBaseUrl.searchParams.set(EXTERNAL_WIDGET_INNER_URL_PARAM, updatedInnerWebviewUrl);

        const updatedOuterDeeplinkUrl = outerDeeplinkBaseUrl.toString();
        return updatedOuterDeeplinkUrl;
    } catch {
        return deeplink;
    }
};
